import { useSyncExternalStore } from "react";

const STORAGE_KEY = "theme";

// Cache the theme value in memory to avoid reading localStorage on every render.
// useSyncExternalStore calls getSnapshot on every render — localStorage.getItem
// is synchronous I/O that we can skip by keeping a module-level cache.
let cachedTheme: "dark" | "light" = (() => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return "light";
})();

function getSnapshot(): "dark" | "light" {
  return cachedTheme;
}

function getServerSnapshot(): "light" {
  return "light";
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function applyTheme(theme: "dark" | "light") {
  // Disable all CSS transitions so every element updates simultaneously
  const css = document.createElement("style");
  css.textContent = "*, *::before, *::after { transition: none !important; }";
  document.head.appendChild(css);

  document.documentElement.classList.toggle("dark", theme === "dark");

  // Force a reflow so the browser applies the no-transition rule before we remove it
  document.documentElement.offsetHeight;

  // Re-enable transitions on the next frame
  requestAnimationFrame(() => {
    document.head.removeChild(css);
  });
}

// Apply on load
applyTheme(cachedTheme);

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggleTheme = () => {
    const next = cachedTheme === "dark" ? "light" : "dark";
    cachedTheme = next;
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
    listeners.forEach((cb) => cb());
  };

  return { theme, toggleTheme };
}
